import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { FileText, ChevronDown, ChevronUp, Save, Loader2, User } from "lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

interface Note {
  id: string;
  authorId: string;
  content: string;
  updatedAt: string;
}

interface SessionNotePanelProps {
  sessionId: string;
  otherPartyLabel: string;
}

export function SessionNotePanel({ sessionId, otherPartyLabel }: SessionNotePanelProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [myNote, setMyNote] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [otherNote, setOtherNote] = useState<Note | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const data = await apiFetch<Note[]>(`/sessions/${sessionId}/notes`);
      setNotes(data);
      const mine = data.find(n => n.authorId === user?.id);
      const theirs = data.find(n => n.authorId !== user?.id);
      if (mine) { setMyNote(mine.content); setSavedNote(mine.content); }
      if (theirs) setOtherNote(theirs);
      setLoaded(true);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [sessionId, user?.id, loaded]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const hasNote = savedNote.length > 0;
  const hasOtherNote = !!otherNote;
  const isDirty = myNote !== savedNote;

  async function save() {
    setSaving(true);
    try {
      await apiFetch(`/sessions/${sessionId}/notes`, {
        method: "PUT",
        body: JSON.stringify({ content: myNote }),
      });
      setSavedNote(myNote);
      toast({ title: myNote.trim() ? "Note saved" : "Note cleared" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pt-2 border-t border-white/5">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors w-full text-left"
      >
        <FileText className={cn("w-4 h-4 shrink-0", (hasNote || hasOtherNote) ? "text-primary" : "")} />
        <span className="flex-1">
          Session Notes
          {(hasNote || hasOtherNote) && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-medium">
              {[hasNote && "you", hasOtherNote && otherPartyLabel].filter(Boolean).join(" & ")}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 py-3">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading notes…</span>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Your note
                </p>
                <textarea
                  value={myNote}
                  onChange={e => setMyNote(e.target.value)}
                  placeholder="Add a private note about this session — techniques drilled, things to remember, follow-up goals…"
                  rows={3}
                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-muted-foreground resize-none focus:outline-none focus:border-primary/50 transition-colors"
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving || !isDirty}
                    className="bg-primary text-primary-foreground hover:bg-primary/80 h-8"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                    {saving ? "Saving…" : "Save Note"}
                  </Button>
                  {savedNote && (
                    <span className="text-xs text-muted-foreground">
                      {isDirty ? "Unsaved changes" : "Saved"}
                    </span>
                  )}
                </div>
              </div>

              {otherNote && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-primary" />
                    <span className="capitalize">{otherPartyLabel}'s note</span>
                    <span className="text-white/20 font-normal">· {format(parseISO(otherNote.updatedAt), "MMM d")}</span>
                  </p>
                  <div className="p-3 rounded-lg bg-white/5 border border-white/8 text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
                    {otherNote.content}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
